import { useState, useRef, useCallback } from 'react'

interface UseVoiceRecorderReturn {
  isRecording: boolean
  isProcessing: boolean
  error: string | null
  transcript: string | null
  startRecording: () => Promise<void>
  stopRecording: () => Promise<string | null>
  reset: () => void
}

export const useVoiceRecorder = (): UseVoiceRecorderReturn => {
  const [isRecording, setIsRecording] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [transcript, setTranscript] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)

  const startRecording = useCallback(async () => {
    try {
      setError(null)
      setTranscript(null)
      audioChunksRef.current = []

      // Check if MediaRecorder is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Votre navigateur ne supporte pas l\'enregistrement audio')
      }

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus' // Use webm for better browser support
      })
      mediaRecorderRef.current = mediaRecorder

      // Collect audio chunks
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      // Handle errors
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event)
        setError('Erreur lors de l\'enregistrement audio')
        setIsRecording(false)
      }

      // Start recording
      mediaRecorder.start(100) // Collect data every 100ms
      setIsRecording(true)

    } catch (err: any) {
      console.error('Error starting recording:', err)
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Permission microphone refusée. Veuillez autoriser l\'accès au microphone.')
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('Aucun microphone trouvé. Veuillez connecter un microphone.')
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Le microphone est déjà utilisé par une autre application.')
      } else {
        setError(err.message || 'Erreur lors du démarrage de l\'enregistrement')
      }
      
      setIsRecording(false)
    }
  }, [])

  const stopRecording = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || !isRecording) {
        resolve(null)
        return
      }

      const mediaRecorder = mediaRecorderRef.current

      // Stop recording
      mediaRecorder.onstop = async () => {
        setIsRecording(false)
        setIsProcessing(true)

        try {
          // Stop all tracks
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop())
            streamRef.current = null
          }

          // Combine audio chunks
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
          
          // Check if we have audio data
          if (audioBlob.size === 0) {
            setError('Aucun audio enregistré')
            setIsProcessing(false)
            resolve(null)
            return
          }

              // Convert blob to base64
              const reader = new FileReader()
              reader.onloadend = async () => {
                try {
                  const base64Audio = (reader.result as string).split(',')[1] // Remove data:audio/webm;base64, prefix

              // Get Supabase URL and keys
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
              const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

              if (!supabaseUrl || !supabaseAnonKey) {
                throw new Error('Configuration Supabase manquante')
              }

              // Get user session for authentication (required for rate limiting)
              const { data: { session } } = await (await import('../lib/supabase')).supabase.auth.getSession()
              if (!session) {
                // Silent failure - return empty transcript
                setTranscript("")
                setError(null)
                setIsProcessing(false)
                resolve(null)
                return
              }

              // Call transcription edge function with user auth token
              const response = await fetch(`${supabaseUrl}/functions/v1/transcribe-audio`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${session.access_token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  audio: base64Audio,
                  language: 'fr', // Default to French, can be made configurable
                }),
              })

              if (!response.ok) {
                // Silent failure - return empty transcript
                setTranscript("")
                setError(null)
                setIsProcessing(false)
                resolve(null)
                return
              }

              const data = await response.json()
              
              // Handle silent failures (empty transcript = rate limit or error)
              if (!data.success || !data.transcript || data.transcript.trim() === "") {
                // Silent failure - no error message shown
                setTranscript("")
                setError(null)
                setIsProcessing(false)
                resolve(null)
                return
              }

              setTranscript(data.transcript)
              setError(null)
              setIsProcessing(false)
              resolve(data.transcript)

            } catch (err: any) {
              console.error('Transcription error:', err)
              setError(err.message || 'Erreur lors de la transcription')
              setIsProcessing(false)
              resolve(null)
            }
          }

          reader.onerror = () => {
            setError('Erreur lors de la lecture de l\'audio')
            setIsProcessing(false)
            resolve(null)
          }

          reader.readAsDataURL(audioBlob)

        } catch (err: any) {
          console.error('Error processing audio:', err)
          setError(err.message || 'Erreur lors du traitement de l\'audio')
          setIsProcessing(false)
          resolve(null)
        }
      }

      // Stop the recorder
      if (mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop()
      }
    })
  }, [isRecording])

  const reset = useCallback(() => {
    // Stop recording if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }

    // Stop stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    // Reset state
    setIsRecording(false)
    setIsProcessing(false)
    setError(null)
    setTranscript(null)
    audioChunksRef.current = []
    mediaRecorderRef.current = null
  }, [])

  return {
    isRecording,
    isProcessing,
    error,
    transcript,
    startRecording,
    stopRecording,
    reset,
  }
}
